import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BusFront, Plus } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { Pagination } from '@/components/tables/Pagination';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { maintenanceApi, vehiclesApi, vehicleTrackingApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  MaintenanceType,
  TransportDocument,
  VehicleDocumentType,
} from '@/types/transport';
import { formatDate, formatDateTime, humanise, orDash } from '@/utils/format';
import {
  Badge,
  ComplianceBadge,
  Field,
  FreshnessBadge,
  MaintenanceStatusBadge,
  Money,
  Stat,
  VehicleStatusBadge,
  ageText,
} from './shared';

const DOCUMENT_TYPES: VehicleDocumentType[] = [
  'REGISTRATION',
  'INSURANCE',
  'INSPECTION_CERTIFICATE',
  'FITNESS_CERTIFICATE',
  'ROUTE_PERMIT',
  'TAX',
  'OTHER',
];

const MAINTENANCE_TYPES: MaintenanceType[] = [
  'ROUTINE_SERVICE',
  'OIL_CHANGE',
  'TIRE_REPLACEMENT',
  'BRAKE_SERVICE',
  'ENGINE_REPAIR',
  'ELECTRICAL_REPAIR',
  'BODY_REPAIR',
  'INSPECTION',
  'OTHER',
];

/** Bytes, in the units a person reads. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "every 6 months or 10,000 km" — a schedule's interval as a sentence. */
function intervalText(months?: number, km?: number): string {
  const parts: string[] = [];
  if (months !== undefined) {
    parts.push(`${months} month${months === 1 ? '' : 's'}`);
  }
  if (km !== undefined) {
    parts.push(`${km.toLocaleString()} km`);
  }
  return parts.length === 0 ? '—' : `every ${parts.join(' or ')}`;
}

/**
 * One vehicle: its details, its papers, its service life, and where it last
 * was.
 *
 * <p>Every judgement on this page is the server's. A paper's compliance state
 * is derived there against the school's warning window; what is due comes
 * computed from the schedules; a position's freshness is the server's verdict,
 * never softened — old data is not presented as live. The page renders those
 * verdicts and calculates nothing (§38).
 *
 * <p>There is no map. The last known position is coordinates, speed and age in
 * words (§37).
 */
export function VehicleDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const seesDocuments = can(PERMISSIONS.VEHICLE_DOCUMENT_VIEW);
  const managesDocuments = can(PERMISSIONS.VEHICLE_DOCUMENT_MANAGE);
  const seesMaintenance = can(PERMISSIONS.VEHICLE_MAINTENANCE_VIEW);
  const managesMaintenance = can(PERMISSIONS.VEHICLE_MAINTENANCE_MANAGE);
  const seesTracking = can(PERMISSIONS.TRANSPORT_TRACKING_VIEW);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<VehicleDocumentType>('REGISTRATION');
  const [title, setTitle] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [documentNotes, setDocumentNotes] = useState('');
  const [deleting, setDeleting] = useState<TransportDocument | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType>('ROUTINE_SERVICE');
  const [intervalMonths, setIntervalMonths] = useState('');
  const [intervalKm, setIntervalKm] = useState('');
  const [lastServiceDate, setLastServiceDate] = useState('');
  const [lastServiceMileage, setLastServiceMileage] = useState('');
  const [scheduleNotes, setScheduleNotes] = useState('');
  const [removingSchedule, setRemovingSchedule] = useState<string | null>(null);

  const [historyPage, setHistoryPage] = useState(0);

  const vehicle = useQuery({
    queryKey: queryKeys.vehicles.detail(id),
    queryFn: () => vehiclesApi.get(id),
  });

  const documents = useQuery({
    queryKey: queryKeys.vehicles.documents(id),
    queryFn: () => vehiclesApi.documents(id),
    enabled: seesDocuments,
  });

  const schedules = useQuery({
    queryKey: queryKeys.vehicles.schedules(id),
    queryFn: () => maintenanceApi.schedules(id),
    enabled: seesMaintenance,
  });

  const historyParams = { page: historyPage, size: 10, sort: 'serviceDate,desc', vehicleId: id };
  const history = useQuery({
    queryKey: queryKeys.maintenance.list(historyParams),
    queryFn: () => maintenanceApi.list(historyParams),
    enabled: seesMaintenance,
  });

  const location = useQuery({
    queryKey: queryKeys.vehicles.location(id),
    queryFn: () => vehicleTrackingApi.lastKnown(id),
    enabled: seesTracking,
  });

  const invalidateDocuments = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
  };

  const upload = useMutation({
    mutationFn: () => {
      if (file === null) {
        throw new Error('Choose a file first');
      }
      return vehiclesApi.uploadDocument(id, file, {
        documentType,
        title: title.trim() === '' ? undefined : title.trim(),
        documentNumber: documentNumber.trim() === '' ? undefined : documentNumber.trim(),
        issuingAuthority: issuingAuthority.trim() === '' ? undefined : issuingAuthority.trim(),
        issuedDate: issuedDate === '' ? undefined : issuedDate,
        expiryDate: expiryDate === '' ? undefined : expiryDate,
        notes: documentNotes.trim() === '' ? undefined : documentNotes.trim(),
      });
    },
    onSuccess: () => {
      toast.success('Document uploaded');
      setUploadOpen(false);
      setFile(null);
      setTitle('');
      setDocumentNumber('');
      setIssuingAuthority('');
      setIssuedDate('');
      setExpiryDate('');
      setDocumentNotes('');
      invalidateDocuments();
    },
    onError: (error) => handleApiError(error),
  });

  const removeDocument = useMutation({
    mutationFn: (documentId: string) => vehiclesApi.deleteDocument(id, documentId),
    onSuccess: () => {
      toast.success('Document removed');
      setDeleting(null);
      invalidateDocuments();
    },
    onError: (error) => {
      handleApiError(error);
      setDeleting(null);
    },
  });

  const saveSchedule = useMutation({
    mutationFn: () =>
      maintenanceApi.saveSchedule(id, {
        maintenanceType,
        intervalMonths: intervalMonths.trim() === '' ? undefined : Number(intervalMonths),
        intervalKm: intervalKm.trim() === '' ? undefined : Number(intervalKm),
        lastServiceDate: lastServiceDate === '' ? undefined : lastServiceDate,
        lastServiceMileage:
          lastServiceMileage.trim() === '' ? undefined : Number(lastServiceMileage),
        notes: scheduleNotes.trim() === '' ? undefined : scheduleNotes.trim(),
      }),
    onSuccess: () => {
      toast.success('Schedule saved');
      setScheduleOpen(false);
      setIntervalMonths('');
      setIntervalKm('');
      setLastServiceDate('');
      setLastServiceMileage('');
      setScheduleNotes('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.schedules(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    },
    onError: (error) => handleApiError(error),
  });

  const removeSchedule = useMutation({
    mutationFn: (scheduleId: string) => maintenanceApi.deleteSchedule(id, scheduleId),
    onSuccess: () => {
      toast.success('Schedule removed');
      setRemovingSchedule(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.schedules(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    },
    onError: (error) => {
      handleApiError(error);
      setRemovingSchedule(null);
    },
  });

  if (vehicle.isError) {
    return <ErrorState error={vehicle.error} onRetry={() => void vehicle.refetch()} />;
  }
  if (vehicle.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = vehicle.data;
  const position = location.data;

  return (
    <div>
      <Link
        to={ROUTES.vehicles}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Vehicles
      </Link>

      <PageHeader
        title={data.displayName}
        description={`${data.registrationNumber}${
          data.make === undefined ? '' : ` · ${[data.make, data.model].filter((part) => part !== undefined).join(' ')}`
        }`}
        icon={<BusFront className="h-5 w-5" aria-hidden="true" />}
        action={<VehicleStatusBadge status={data.status} />}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Seats" value={data.seatingCapacity} />
        <Stat label="Odometer" value={`${data.currentMileageKm.toLocaleString()} km`} />
        <Stat
          label="Papers"
          value={<ComplianceBadge state={data.documentCompliance} />}
          hint="The worst state among this vehicle's dated papers."
          tone={
            data.documentCompliance === 'EXPIRED'
              ? 'danger'
              : data.documentCompliance === 'EXPIRING'
                ? 'warning'
                : data.documentCompliance === 'VALID'
                  ? 'success'
                  : 'neutral'
          }
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Details</h2>
            <dl className="mt-2">
              <Field label="Registration">
                <span className="font-mono">{data.registrationNumber}</span>
              </Field>
              <Field label="Fleet number">{orDash(data.fleetNumber)}</Field>
              <Field label="Type">{humanise(data.vehicleType)}</Field>
              <Field label="Make">{orDash(data.make)}</Field>
              <Field label="Model">{orDash(data.model)}</Field>
              <Field label="Year">{orDash(data.modelYear)}</Field>
              <Field label="Colour">{orDash(data.color)}</Field>
              <Field label="Fuel">{humanise(data.fuelType)}</Field>
              <Field label="Campus">{data.campusName ?? 'Any campus'}</Field>
              <Field label="Acquired">{formatDate(data.acquiredOn)}</Field>
              <Field label="Registered">{formatDateTime(data.createdAt)}</Field>
            </dl>
            {data.notes !== undefined && (
              <p className="mt-3 border-t border-line pt-3 text-sm text-muted">{data.notes}</p>
            )}
          </Card>

          {seesTracking && (
            <Card className="p-4">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Last known position
                </h2>
                {position !== undefined && <FreshnessBadge freshness={position.freshness} />}
              </div>
              <p className="mt-0.5 text-xs text-muted">
                As far as anything has told us — old data is never presented as live.
              </p>
              {location.isPending ? (
                <LoadingState label="Loading position" />
              ) : location.error !== null ? (
                <ErrorState error={location.error} onRetry={() => void location.refetch()} />
              ) : position === undefined || position.freshness === 'UNAVAILABLE' ? (
                <p className="py-6 text-center text-sm text-muted">
                  Nothing has ever reported a position for this vehicle.
                </p>
              ) : (
                <dl className="mt-2">
                  <Field label="Reported">
                    {formatDateTime(position.recordedAt)}
                    <span className="ml-1.5 text-xs text-subtle">
                      ({ageText(position.ageSeconds)})
                    </span>
                  </Field>
                  <Field label="Coordinates">
                    {position.latitude === undefined || position.longitude === undefined ? (
                      '—'
                    ) : (
                      <span className="font-mono text-xs">
                        {position.latitude.toFixed(5)}, {position.longitude.toFixed(5)}
                      </span>
                    )}
                  </Field>
                  <Field label="Speed">
                    {position.speedKph === undefined ? '—' : `${position.speedKph} km/h`}
                  </Field>
                  <Field label="Heading">
                    {position.headingDegrees === undefined ? '—' : `${position.headingDegrees}°`}
                  </Field>
                </dl>
              )}
            </Card>
          )}
        </div>

        <div className="space-y-4">
          {seesDocuments && (
            <Card>
              <CardHeader
                title="Papers"
                description="Registration, insurance, fitness — each with the server's verdict on its expiry."
                action={
                  managesDocuments ? (
                    <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Upload
                    </Button>
                  ) : undefined
                }
              />
              <div className="px-5 pb-4">
                {documents.isPending ? (
                  <LoadingState label="Loading documents" />
                ) : documents.error !== null ? (
                  <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
                ) : (documents.data ?? []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    No papers on file — which is itself worth knowing.
                  </p>
                ) : (
                  <ul className="divide-y divide-line">
                    {(documents.data ?? []).map((document) => (
                      <li
                        key={document.id}
                        className="flex flex-wrap items-start justify-between gap-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink">
                            {document.title ?? humanise(document.documentType)}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-subtle">
                            {document.fileName} · {formatSize(document.fileSize)}
                            {document.documentNumber !== undefined &&
                              ` · no. ${document.documentNumber}`}
                            {document.issuingAuthority !== undefined &&
                              ` · ${document.issuingAuthority}`}
                          </p>
                          <p className="mt-0.5 text-xs text-subtle">
                            {document.expiryDate === undefined
                              ? 'no expiry on file'
                              : `expires ${formatDate(document.expiryDate)}`}
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {/* Derived at read time against the school's warning
                              window — the server's verdict, never re-derived here. */}
                          <ComplianceBadge state={document.state} />
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={downloading === document.id}
                            onClick={() => {
                              setDownloading(document.id);
                              void vehiclesApi
                                .downloadDocument(id, document.id, document.fileName)
                                .catch((error: unknown) => handleApiError(error))
                                .finally(() => setDownloading(null));
                            }}
                          >
                            Download
                          </Button>
                          {managesDocuments && (
                            <Button size="sm" variant="ghost" onClick={() => setDeleting(document)}>
                              Delete
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          )}

          {seesMaintenance && (
            <Card>
              <CardHeader
                title="Service intervals"
                description="Every N months and/or kilometres — whichever arrives first. What is due is computed on the server."
                action={
                  managesMaintenance ? (
                    <Button size="sm" variant="secondary" onClick={() => setScheduleOpen(true)}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Set an interval
                    </Button>
                  ) : undefined
                }
              />
              <div className="px-5 pb-4">
                {schedules.isPending ? (
                  <LoadingState label="Loading schedules" />
                ) : schedules.error !== null ? (
                  <ErrorState error={schedules.error} onRetry={() => void schedules.refetch()} />
                ) : (schedules.data ?? []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    No standing intervals — nothing will ever show as due for this vehicle.
                  </p>
                ) : (
                  <ul className="divide-y divide-line">
                    {(schedules.data ?? []).map((schedule) => (
                      <li
                        key={schedule.id}
                        className="flex flex-wrap items-start justify-between gap-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink">
                            {humanise(schedule.maintenanceType)}
                          </p>
                          <p className="mt-0.5 text-xs text-subtle">
                            {intervalText(schedule.intervalMonths, schedule.intervalKm)}
                            {schedule.lastServiceDate !== undefined &&
                              ` · last ${formatDate(schedule.lastServiceDate)}`}
                          </p>
                          <p className="mt-0.5 text-xs text-subtle">
                            {schedule.nextDueDate === undefined &&
                            schedule.nextDueMileage === undefined
                              ? 'next due not computable yet'
                              : `next due ${[
                                  schedule.nextDueDate === undefined
                                    ? undefined
                                    : formatDate(schedule.nextDueDate),
                                  schedule.nextDueMileage === undefined
                                    ? undefined
                                    : `at ${schedule.nextDueMileage.toLocaleString()} km`,
                                ]
                                  .filter((part) => part !== undefined)
                                  .join(' or ')}`}
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {schedule.overdue && <Badge tone="danger">overdue</Badge>}
                          {managesMaintenance && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRemovingSchedule(schedule.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          )}

          {seesMaintenance && (
            <Card>
              <CardHeader
                title="Maintenance history"
                description="Every service this vehicle has had. Booked and edited from the Maintenance screen."
              />
              {history.isPending ? (
                <LoadingState label="Loading history" />
              ) : history.error !== null ? (
                <div className="px-5 pb-4">
                  <ErrorState error={history.error} onRetry={() => void history.refetch()} />
                </div>
              ) : (history.data?.data ?? []).length === 0 ? (
                <p className="px-5 py-6 text-center text-sm text-muted">
                  No services recorded for this vehicle yet.
                </p>
              ) : (
                <>
                  <ul className="divide-y divide-line px-5">
                    {(history.data?.data ?? []).map((record) => (
                      <li
                        key={record.id}
                        className="flex flex-wrap items-start justify-between gap-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink">
                            {humanise(record.maintenanceType)}
                          </p>
                          <p className="mt-0.5 text-xs text-subtle">
                            {formatDate(record.serviceDate)}
                            {record.serviceProvider !== undefined && ` · ${record.serviceProvider}`}
                            {record.mileageKm !== undefined &&
                              ` · at ${record.mileageKm.toLocaleString()} km`}
                          </p>
                          {record.description !== undefined && (
                            <p className="mt-0.5 truncate text-xs text-subtle">
                              {record.description}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          {/* A reporting figure — no payment or ledger entry behind it. */}
                          <Money value={record.totalCost} />
                          <MaintenanceStatusBadge status={record.status} />
                        </div>
                      </li>
                    ))}
                  </ul>
                  <Pagination pagination={history.data?.pagination} onPageChange={setHistoryPage} />
                </>
              )}
            </Card>
          )}
        </div>
      </div>

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload a paper"
        description="The file is stored under a generated name; the name it arrived with is kept as a label only."
        footer={
          <>
            <Button variant="secondary" onClick={() => setUploadOpen(false)} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button onClick={() => upload.mutate()} loading={upload.isPending} disabled={file === null}>
              Upload
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="vehicle-document-file" className="mb-1.5 block text-xs font-medium text-muted">
              File<span className="ml-0.5 text-danger">*</span>
            </label>
            <input
              id="vehicle-document-file"
              ref={fileInput}
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-ink"
            />
          </div>

          <SelectField
            label="Document type"
            required
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value as VehicleDocumentType)}
          >
            {DOCUMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanise(type)}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Optional label"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Document number"
              value={documentNumber}
              onChange={(event) => setDocumentNumber(event.target.value)}
            />
            <TextField
              label="Issuing authority"
              value={issuingAuthority}
              onChange={(event) => setIssuingAuthority(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Issued"
              type="date"
              value={issuedDate}
              onChange={(event) => setIssuedDate(event.target.value)}
            />
            <TextField
              label="Expires"
              type="date"
              value={expiryDate}
              onChange={(event) => setExpiryDate(event.target.value)}
              hint="Left blank, the paper reads as undated — visible, not quietly valid."
            />
          </div>
          <TextField
            label="Notes"
            value={documentNotes}
            onChange={(event) => setDocumentNotes(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        title="Set a service interval"
        description="One schedule per service type; saving again replaces the intervals."
        footer={
          <>
            <Button variant="secondary" onClick={() => setScheduleOpen(false)} disabled={saveSchedule.isPending}>
              Cancel
            </Button>
            <Button onClick={() => saveSchedule.mutate()} loading={saveSchedule.isPending}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <SelectField
            label="Service type"
            required
            value={maintenanceType}
            onChange={(event) => setMaintenanceType(event.target.value as MaintenanceType)}
          >
            {MAINTENANCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanise(type)}
              </option>
            ))}
          </SelectField>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Every N months"
              type="number"
              min={1}
              value={intervalMonths}
              onChange={(event) => setIntervalMonths(event.target.value)}
            />
            <TextField
              label="Every N km"
              type="number"
              min={100}
              value={intervalKm}
              onChange={(event) => setIntervalKm(event.target.value)}
            />
          </div>
          <p className="text-xs text-subtle">
            Months, kilometres, or both — whichever arrives first makes the service due.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Last serviced on"
              type="date"
              value={lastServiceDate}
              onChange={(event) => setLastServiceDate(event.target.value)}
              hint="The anchor the intervals count from; advanced automatically when a matching service completes."
            />
            <TextField
              label="At mileage (km)"
              type="number"
              min={0}
              value={lastServiceMileage}
              onChange={(event) => setLastServiceMileage(event.target.value)}
            />
          </div>
          <TextField
            label="Notes"
            value={scheduleNotes}
            onChange={(event) => setScheduleNotes(event.target.value)}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title="Remove this paper?"
        message={`${deleting?.fileName ?? ''} will be removed from storage. This cannot be undone.`}
        confirmLabel="Remove"
        loading={removeDocument.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            removeDocument.mutate(deleting.id);
          }
        }}
      />

      <ConfirmDialog
        open={removingSchedule !== null}
        destructive
        title="Remove this interval?"
        message="The service will stop showing as due. Past maintenance records are untouched."
        confirmLabel="Remove"
        loading={removeSchedule.isPending}
        onCancel={() => setRemovingSchedule(null)}
        onConfirm={() => {
          if (removingSchedule !== null) {
            removeSchedule.mutate(removingSchedule);
          }
        }}
      />
    </div>
  );
}
